import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Backup config data
  backupConfig: null,
  backupConfigLoading: false,
  backupConfigError: null,
  // Save backup config
  saveLoading: false,
  saveError: null,
  saveSuccess: false,
  saveSuccessMessage: null,
  // Manual backup
  manualBackupLoading: false,
  manualBackupError: null,
  manualBackupSuccess: false,
  manualBackupSuccessMessage: null,
  // Backup logs
  backupLogs: [],
  backupLogsLoading: false,
  backupLogsError: null,
  backupLogsTotal: 0,
  backupLogsPage: 1,
  backupLogsSize: 10,
  // Backup logs filters
  filterFromDate: null,
  filterToDate: null,
  // Restore backup
  restoreLoading: false,
  restoreError: null,
  restoreSuccess: false,
  restoreSuccessMessage: null,
};

const systemBackupSlice = createSlice({
  name: 'systemBackup',
  initialState,
  reducers: {
    // Fetch backup config
    fetchBackupConfigRequest: (state) => {
      state.backupConfigLoading = true;
      state.backupConfigError = null;
    },
    fetchBackupConfigSuccess: (state, action) => {
      state.backupConfigLoading = false;
      state.backupConfig = action.payload;
      state.backupConfigError = null;
    },
    fetchBackupConfigFailure: (state, action) => {
      state.backupConfigLoading = false;
      state.backupConfig = null;
      state.backupConfigError = action.payload;
    },
    // Clear backup config
    clearBackupConfig: (state) => {
      state.backupConfig = null;
      state.backupConfigError = null;
    },
    // Save backup config
    saveBackupConfigRequest: (state) => {
      state.saveLoading = true;
      state.saveError = null;
      state.saveSuccess = false;
    },
    saveBackupConfigSuccess: (state, action) => {
      state.saveLoading = false;
      state.saveSuccess = true;
      state.saveSuccessMessage = action.payload?.message || null;
      state.saveError = null;
    },
    saveBackupConfigFailure: (state, action) => {
      state.saveLoading = false;
      state.saveSuccess = false;
      state.saveError = action.payload;
    },
    // Clear save status
    clearSaveStatus: (state) => {
      state.saveSuccess = false;
      state.saveSuccessMessage = null;
      state.saveError = null;
    },
    // Manual backup
    manualBackupRequest: (state) => {
      state.manualBackupLoading = true;
      state.manualBackupError = null;
      state.manualBackupSuccess = false;
    },
    manualBackupSuccess: (state, action) => {
      state.manualBackupLoading = false;
      state.manualBackupSuccess = true;
      state.manualBackupSuccessMessage = action.payload?.message || null;
      state.manualBackupError = null;
    },
    manualBackupFailure: (state, action) => {
      state.manualBackupLoading = false;
      state.manualBackupSuccess = false;
      state.manualBackupError = action.payload;
    },
    // Clear manual backup status
    clearManualBackupStatus: (state) => {
      state.manualBackupSuccess = false;
      state.manualBackupSuccessMessage = null;
      state.manualBackupError = null;
    },
    // Fetch backup logs
    fetchBackupLogsRequest: (state, action) => {
      state.backupLogsLoading = true;
      state.backupLogsError = null;
      if (action.payload?.page) {
        state.backupLogsPage = action.payload.page;
      }
      if (action.payload?.size) {
        state.backupLogsSize = action.payload.size;
      }
    },
    fetchBackupLogsSuccess: (state, action) => {
      state.backupLogsLoading = false;
      state.backupLogs = action.payload.data || [];
      state.backupLogsTotal = action.payload.meta?.total || 0;
      state.backupLogsError = null;
    },
    fetchBackupLogsFailure: (state, action) => {
      state.backupLogsLoading = false;
      state.backupLogsError = action.payload;
    },
    // Set backup logs page
    setBackupLogsPage: (state, action) => {
      state.backupLogsPage = action.payload;
    },
    // Set backup logs page size
    setBackupLogsSize: (state, action) => {
      state.backupLogsSize = action.payload;
      state.backupLogsPage = 1;
    },
    // Set backup logs filters
    setFilterFromDate: (state, action) => {
      state.filterFromDate = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterToDate: (state, action) => {
      state.filterToDate = action.payload;
      state.backupLogsPage = 1;
    },
    clearFilters: (state) => {
      state.filterFromDate = null;
      state.filterToDate = null;
      state.backupLogsPage = 1;
    },
    // Restore backup
    restoreBackupRequest: (state) => {
      state.restoreLoading = true;
      state.restoreError = null;
      state.restoreSuccess = false;
    },
    restoreBackupSuccess: (state, action) => {
      state.restoreLoading = false;
      state.restoreSuccess = true;
      state.restoreSuccessMessage = action.payload?.message || null;
      state.restoreError = null;
    },
    restoreBackupFailure: (state, action) => {
      state.restoreLoading = false;
      state.restoreSuccess = false;
      state.restoreError = action.payload;
    },
    clearRestoreStatus: (state) => {
      state.restoreSuccess = false;
      state.restoreSuccessMessage = null;
      state.restoreError = null;
    },
  },
});

export const {
  fetchBackupConfigRequest,
  fetchBackupConfigSuccess,
  fetchBackupConfigFailure,
  clearBackupConfig,
  saveBackupConfigRequest,
  saveBackupConfigSuccess,
  saveBackupConfigFailure,
  clearSaveStatus,
  manualBackupRequest,
  manualBackupSuccess,
  manualBackupFailure,
  clearManualBackupStatus,
  fetchBackupLogsRequest,
  fetchBackupLogsSuccess,
  fetchBackupLogsFailure,
  setBackupLogsPage,
  setBackupLogsSize,
  setFilterFromDate,
  setFilterToDate,
  clearFilters,
  restoreBackupRequest,
  restoreBackupSuccess,
  restoreBackupFailure,
  clearRestoreStatus,
} = systemBackupSlice.actions;

// Selectors
export const selectBackupConfig = (state) => state.systemBackup?.backupConfig;
export const selectBackupConfigLoading = (state) => state.systemBackup?.backupConfigLoading || false;
export const selectBackupConfigError = (state) => state.systemBackup?.backupConfigError;
export const selectSaveLoading = (state) => state.systemBackup?.saveLoading || false;
export const selectSaveError = (state) => state.systemBackup?.saveError;
export const selectSaveSuccess = (state) => state.systemBackup?.saveSuccess || false;
export const selectSaveSuccessMessage = (state) => state.systemBackup?.saveSuccessMessage;
export const selectManualBackupLoading = (state) => state.systemBackup?.manualBackupLoading || false;
export const selectManualBackupError = (state) => state.systemBackup?.manualBackupError;
export const selectManualBackupSuccess = (state) => state.systemBackup?.manualBackupSuccess || false;
export const selectManualBackupSuccessMessage = (state) => state.systemBackup?.manualBackupSuccessMessage;
// Backup logs selectors
export const selectBackupLogs = (state) => state.systemBackup?.backupLogs || [];
export const selectBackupLogsLoading = (state) => state.systemBackup?.backupLogsLoading || false;
export const selectBackupLogsError = (state) => state.systemBackup?.backupLogsError;
export const selectBackupLogsTotal = (state) => state.systemBackup?.backupLogsTotal || 0;
export const selectBackupLogsPage = (state) => state.systemBackup?.backupLogsPage || 1;
export const selectBackupLogsSize = (state) => state.systemBackup?.backupLogsSize || 10;
// Filter selectors
export const selectFilterFromDate = (state) => state.systemBackup?.filterFromDate;
export const selectFilterToDate = (state) => state.systemBackup?.filterToDate;
// Restore selectors
export const selectRestoreLoading = (state) => state.systemBackup?.restoreLoading || false;
export const selectRestoreError = (state) => state.systemBackup?.restoreError;
export const selectRestoreSuccess = (state) => state.systemBackup?.restoreSuccess || false;
export const selectRestoreSuccessMessage = (state) => state.systemBackup?.restoreSuccessMessage;

export default systemBackupSlice.reducer;
